import type { LayerRenderer } from '../registry'

/**
 * Pilosité faciale (slot `facialHair`). Peinte APRÈS le buste (le col passe
 * derrière la barbe) et AVANT les cheveux. Mâchoire : arc 46-74 × 54-72.
 */
export const FACIAL_HAIR: Record<string, LayerRenderer> = {
  beard_rase: ({ uid }) => (
    <>
      <defs>
        <linearGradient id={`${uid}-rase`} x1="0" y1="0" x2="0.3" y2="1">
          <stop offset="0" stopColor="#2E1B10" stopOpacity="0.08" />
          <stop offset="1" stopColor="#1F1008" stopOpacity="0.15" />
        </linearGradient>
      </defs>
      {/* voile de rasage : silhouette d'origine, gradient à la place de l'aplat */}
      <path
        d="M 45.5 55 C 46 62 48.5 67.5 53 70.8 C 55.5 72.6 64.5 72.6 67 70.8 C 71.5 67.5 74 62 74.5 55 C 73 60 71.2 63.5 68.6 65.8 C 69 67.6 68.6 69 67.2 69.9 C 64.4 71.6 55.6 71.6 52.8 69.9 C 51.4 69 51 67.6 51.4 65.8 C 48.8 63.5 47 60 45.5 55 Z"
        fill={`url(#${uid}-rase)`}
      />
      {/* densité un peu plus marquée sur le menton */}
      <path d="M 55.5 68.5 C 57.5 69.6 62.5 69.6 64.5 68.5 C 63.8 70.4 61.9 71.3 60 71.3 C 58.1 71.3 56.2 70.4 55.5 68.5 Z" fill="#1F1008" opacity="0.1" />
      {/* grain de rasage : poils épars ultra-fins */}
      <path d="M 51.8 63.5 L 52.6 64.6 M 54.4 66.8 L 55.3 67.8 M 64.8 66.9 L 65.7 65.9 M 67.5 63.6 L 68.3 62.6 M 58.2 69.8 L 58.9 70.8 M 61.4 70.8 L 62.1 69.8" fill="none" stroke="#2E1B10" strokeWidth="0.5" strokeLinecap="round" opacity="0.12" />
      {/* reflet discret côté gauche (lumière haut-gauche) */}
      <path d="M 48.4 60.5 C 49.6 63.4 51.2 65.7 53.2 67.3" fill="none" stroke="#F8DDB6" strokeWidth="0.8" strokeLinecap="round" opacity="0.12" />
    </>
  ),
  beard_courte: () => (
    <path d="M 46 54 C 46.6 62 49.5 68 54 70.5 C 57.5 72.5 62.5 72.5 66 70.5 C 70.5 68 73.4 62 74 54 C 72 58 70 60.5 68 62 C 68.8 64.5 67.8 66.6 65.5 67.8 C 62.5 69.3 57.5 69.3 54.5 67.8 C 52.2 66.6 51.2 64.5 52 62 C 50 60.5 48 58 46 54 Z" fill="#2E1B10" />
  ),
  beard_bouc: ({ uid }) => (
    <>
      <defs>
        <linearGradient id={`${uid}-bouc`} x1="0" y1="0" x2="0.4" y2="1">
          <stop offset="0" stopColor="#4A2B16" />
          <stop offset="1" stopColor="#1F1008" />
        </linearGradient>
      </defs>
      <ellipse cx="60" cy="78.6" rx="5.2" ry="1.6" fill="#1A0D06" opacity="0.28" />
      <path
        fillRule="evenodd"
        d="M 52.5 62.5 C 55 61 58 60.8 60 61.8 C 62 60.8 65 61 67.5 62.5 C 68.6 65.8 68.4 69.3 66.8 72 C 65.2 74.6 62.6 76.4 60 77.6 C 57.4 76.4 54.8 74.6 53.2 72 C 51.6 69.3 51.4 65.8 52.5 62.5 Z M 56 65.2 C 58.4 64.4 61.6 64.4 64 65.2 C 64.6 67.2 63.8 69 62 69.8 C 60.8 70.3 59.2 70.3 58 69.8 C 56.2 69 55.4 67.2 56 65.2 Z"
        fill={`url(#${uid}-bouc)`}
      />
      {/* ombre interne côté droit (volume du bouc) */}
      <path d="M 66.9 63.4 C 68 66.2 67.9 69.2 66.4 71.7 C 65.1 74 62.9 75.9 60.6 77.1 C 62.7 74.9 64.3 72.5 65.1 69.9 C 65.9 67.5 66 65.3 65.5 63.6 Z" fill="#140A04" opacity="0.5" />
      {/* reflet supérieur gauche */}
      <path d="M 53.4 63.6 C 54.6 62.4 56.4 61.7 58.2 61.7 C 56.6 62.8 55.4 64.3 54.8 66.2 C 54 65.5 53.5 64.6 53.4 63.6 Z" fill="#7E5230" opacity="0.55" />
      {/* mèches internes sombres (hors de l'ouverture de bouche) */}
      <path d="M 54.4 64.3 C 54 66.8 54.4 69.4 55.6 71.6 M 57.6 71.4 C 58.4 73.2 59.2 74.6 60.2 75.8 M 62.4 71.2 C 62 73.2 61.4 74.8 60.6 76.2" fill="none" stroke="#140A04" strokeWidth="0.8" strokeLinecap="round" opacity="0.55" />
      {/* reflets de mèche */}
      <path d="M 53.6 63.2 C 55.4 62.1 57.6 61.8 59.4 62.4" fill="none" stroke="#6B4226" strokeWidth="1" strokeLinecap="round" opacity="0.8" />
      <path d="M 57.2 71.6 C 58.8 73.4 59.6 74.6 60 75.6" fill="none" stroke="#6B4226" strokeWidth="0.9" strokeLinecap="round" opacity="0.6" />
      {/* rim light bord droit */}
      <path d="M 67.8 64.5 C 68.3 68 67.6 71.4 65.6 73.8" fill="none" stroke="#E5CC8F" strokeWidth="1" strokeLinecap="round" opacity="0.55" />
    </>
  ),
  beard_moustache_fine: ({ uid }) => (
    <>
      <defs>
        <linearGradient id={`${uid}-mfine`} x1="0" y1="0" x2="0.2" y2="1">
          <stop offset="0" stopColor="#4A2B16" />
          <stop offset="1" stopColor="#1F1008" />
        </linearGradient>
      </defs>
      {/* ombre portée fine sous chaque aile (évite le centre de la bouche) */}
      <path d="M 51.6 64.8 C 54 65.4 56.6 65.5 59 65.2 C 56.6 66.2 53.8 66.1 51.6 64.8 Z" fill="#2E1B10" opacity="0.25" />
      <path d="M 68.4 64.8 C 66 65.4 63.4 65.5 61 65.2 C 63.4 66.2 66.2 66.1 68.4 64.8 Z" fill="#2E1B10" opacity="0.25" />
      {/* deux ailes, silhouette d'origine, en gradient */}
      <path
        d="M 59.4 63.4 C 56.8 62.2 54.2 62.4 52.2 63.8 C 51.2 64.5 50.2 64.4 49.3 63.4 C 49.8 65.1 51 65.9 52.6 65.5 C 55 64.9 57.2 64.6 59.4 64.6 Z"
        fill={`url(#${uid}-mfine)`}
      />
      <path
        d="M 60.6 63.4 C 63.2 62.2 65.8 62.4 67.8 63.8 C 68.8 64.5 69.8 64.4 70.7 63.4 C 70.2 65.1 69 65.9 67.4 65.5 C 65 64.9 62.8 64.6 60.6 64.6 Z"
        fill={`url(#${uid}-mfine)`}
      />
      {/* mèche interne sombre le long du bord inférieur */}
      <path d="M 53 64.9 C 55 64.4 57.2 64.2 59.2 64.3 M 67 64.9 C 65 64.4 62.8 64.2 60.8 64.3" fill="none" stroke="#140A04" strokeWidth="0.6" strokeLinecap="round" opacity="0.5" />
      {/* reflets supérieurs */}
      <path d="M 52.6 63.4 C 54.6 62.7 56.8 62.8 58.6 63.6" fill="none" stroke="#6B4226" strokeWidth="0.8" strokeLinecap="round" opacity="0.7" />
      <path d="M 61.4 63.6 C 63 62.9 64.8 62.8 66.4 63.2" fill="none" stroke="#6B4226" strokeWidth="0.7" strokeLinecap="round" opacity="0.55" />
      {/* pointes cirées */}
      <circle cx="49.6" cy="64.2" r="0.5" fill="#1F1008" />
      <circle cx="70.4" cy="64.2" r="0.5" fill="#1F1008" />
      {/* rim light pointe droite */}
      <path d="M 66.2 63.4 C 67.4 63.5 68.6 63.9 69.6 64.5" fill="none" stroke="#E5CC8F" strokeWidth="0.9" strokeLinecap="round" opacity="0.6" />
    </>
  ),
  beard_tressee: ({ uid }) => (
    <>
      <defs>
        <linearGradient id={`${uid}-braid`} x1="0" y1="0" x2="0.35" y2="1">
          <stop offset="0" stopColor="#4A2B16" />
          <stop offset="1" stopColor="#20100A" />
        </linearGradient>
      </defs>
      <path
        fillRule="evenodd"
        d="M 45.5 53 C 45 61 46.3 68.5 49.5 73.5 C 52.3 77.8 55.8 80.2 60 80.8 C 64.2 80.2 67.7 77.8 70.5 73.5 C 73.7 68.5 75 61 74.5 53 C 72.8 58.5 70.8 62 68.2 64 C 65.9 61.9 63.2 61 60 61 C 56.8 61 54.1 61.9 51.8 64 C 49.2 62 47.2 58.5 45.5 53 Z M 56.5 65.6 C 58.6 65 61.4 65 63.5 65.6 C 63.9 67.2 63.2 68.5 61.6 69 C 60.6 69.3 59.4 69.3 58.4 69 C 56.8 68.5 56.1 67.2 56.5 65.6 Z"
        fill={`url(#${uid}-braid)`}
      />
      {/* ombre interne côté droit (volume de la masse) */}
      <path d="M 72.2 58 C 72.6 63.6 71.6 68.6 69.2 72.4 C 67 75.9 64.2 78.3 60.9 79.7 C 63.9 77.2 66.3 74.2 67.9 70.6 C 69.5 67 70.3 62.8 70.2 58.2 Z" fill="#140A05" opacity="0.45" />
      {/* reflet supérieur gauche */}
      <path d="M 47.3 56.5 C 48.4 59.7 49.9 62.1 52 63.9 C 50.6 64.7 49.2 64.2 48 62.5 C 46.8 60.7 46.5 58.7 47.3 56.5 Z" fill="#7E5230" opacity="0.5" />
      <path d="M 52.6 74.5 C 51.6 77.5 51.5 80.6 52.6 83.2 C 53.3 84.7 55 84.7 55.7 83.2 C 56.8 80.6 56.7 77.5 55.7 74.5 Z" fill={`url(#${uid}-braid)`} />
      <path d="M 64.3 74.5 C 63.3 77.5 63.2 80.6 64.3 83.2 C 65 84.7 66.7 84.7 67.4 83.2 C 68.5 80.6 68.4 77.5 67.4 74.5 Z" fill={`url(#${uid}-braid)`} />
      {/* torsades des tresses */}
      <path d="M 52.3 77.5 L 56 78.2 M 63.9 77.5 L 67.7 78.2" fill="none" stroke="#150A05" strokeWidth="0.9" strokeLinecap="round" opacity="0.7" />
      <path d="M 54.3 75.2 C 53.8 77.5 53.8 80.3 54.3 82.6 M 66 75.2 C 65.5 77.5 65.5 80.3 66 82.6" fill="none" stroke="#150A05" strokeWidth="0.7" strokeLinecap="round" opacity="0.55" />
      {/* anneaux : base or + ombre basse + filet clair + éclat métal */}
      <rect x="52" y="79.4" width="4.4" height="2.6" rx="1.2" fill="#C9A55C" />
      <rect x="63.7" y="79.4" width="4.4" height="2.6" rx="1.2" fill="#C9A55C" />
      <path d="M 52.7 81.5 L 55.7 81.5 M 64.4 81.5 L 67.4 81.5" fill="none" stroke="#8A6A34" strokeWidth="0.8" strokeLinecap="round" />
      <path d="M 52.6 80.1 L 55.8 80.1 M 64.3 80.1 L 67.5 80.1" fill="none" stroke="#E5CC8F" strokeWidth="0.8" strokeLinecap="round" />
      <circle cx="53.1" cy="79.9" r="0.5" fill="#F7EFDE" opacity="0.95" />
      <circle cx="64.8" cy="79.9" r="0.5" fill="#F7EFDE" opacity="0.95" />
      {/* mèches internes */}
      <path d="M 49.4 60 C 50.4 66 52.4 71.2 55.6 74.6" fill="none" stroke="#5C3A20" strokeWidth="1.1" strokeLinecap="round" opacity="0.7" />
      <path d="M 55 63.2 C 57 74 58 76.4 60 78.6" fill="none" stroke="#5C3A20" strokeWidth="0.9" strokeLinecap="round" opacity="0.5" />
      <path d="M 65.2 63.6 C 66 68.2 65.8 72.4 64.4 76" fill="none" stroke="#140A05" strokeWidth="0.9" strokeLinecap="round" opacity="0.5" />
      {/* rim light bord droit (masse + tresse droite) */}
      <path d="M 73.8 57 C 73.6 63.5 72.2 69.8 69.6 73.6" fill="none" stroke="#E5CC8F" strokeWidth="1" strokeLinecap="round" opacity="0.5" />
      <path d="M 67.3 75.4 C 68.1 77.8 68.1 80.6 67.3 82.9" fill="none" stroke="#E5CC8F" strokeWidth="0.8" strokeLinecap="round" opacity="0.5" />
    </>
  ),
  beard_grise_sel: ({ uid }) => (
    <>
      <defs>
        <linearGradient id={`${uid}-sel`} x1="0" y1="0" x2="0.35" y2="1">
          <stop offset="0" stopColor="#3A2413" />
          <stop offset="1" stopColor="#1F1008" />
        </linearGradient>
      </defs>
      <ellipse cx="60" cy="76.6" rx="6.5" ry="1.6" fill="#1A0D06" opacity="0.25" />
      <path
        fillRule="evenodd"
        d="M 45.8 54 C 45.4 61.5 47.3 67.8 51.4 71.6 C 54 74 57 75.2 60 75.4 C 63 75.2 66 74 68.6 71.6 C 72.7 67.8 74.6 61.5 74.2 54 C 72.6 59 70.7 62.4 68.2 64.4 C 66 62.6 63.2 61.8 60 61.8 C 56.8 61.8 54 62.6 51.8 64.4 C 49.3 62.4 47.4 59 45.8 54 Z M 56.4 65.4 C 58.5 64.8 61.5 64.8 63.6 65.4 C 64 67 63.3 68.3 61.7 68.8 C 60.6 69.1 59.4 69.1 58.3 68.8 C 56.7 68.3 56 67 56.4 65.4 Z"
        fill={`url(#${uid}-sel)`}
      />
      {/* ombre interne côté droit (volume) */}
      <path d="M 71.8 58.2 C 72 62.6 70.9 66.4 68.4 69.4 C 66.3 71.9 63.6 73.6 60.8 74.5 C 63.4 72.8 65.6 70.6 67.1 68 C 68.7 65.2 69.6 62 69.7 58.4 Z" fill="#140A05" opacity="0.45" />
      {/* reflet supérieur (haut de la masse) */}
      <path d="M 50.6 62.6 C 52.8 61.2 55.4 60.6 58.2 60.7 C 55.6 61.6 53.6 62.9 52.2 64.7 C 51.5 64.1 51 63.4 50.6 62.6 Z" fill="#6B4226" opacity="0.5" />
      {/* mèches grises (sel) */}
      <path d="M 48.2 58 C 48.7 62.5 50.2 66 52.8 68.4 C 51.2 68.8 49.9 68.2 48.9 66.4 C 47.6 63.9 47.3 60.9 48.2 58 Z" fill="#B9B4A8" opacity="0.85" />
      <path d="M 57 70.8 C 58.8 71.8 61.2 71.8 63 70.8 C 62.6 73 61.4 74.2 60 74.4 C 58.6 74.2 57.4 73 57 70.8 Z" fill="#B9B4A8" opacity="0.8" />
      <path d="M 62.5 62.4 C 64.6 62.2 66.4 62.8 67.6 64.1 C 66.2 64.7 64.4 64.4 62.5 63.4 Z" fill="#B9B4A8" opacity="0.7" />
      <path d="M 54.6 63.6 C 54 66.2 54.1 68.8 54.9 71.2 M 65.6 66 C 65.9 68 65.4 69.9 64.2 71.6" fill="none" stroke="#D8D3C6" strokeWidth="0.7" strokeLinecap="round" opacity="0.55" />
      {/* mèches internes sombres */}
      <path d="M 50.6 65.4 C 52 68.4 54 70.8 56.6 72.4 M 68.6 61.4 C 68 65.4 66.6 68.8 64.4 71.4" fill="none" stroke="#140A05" strokeWidth="0.8" strokeLinecap="round" opacity="0.5" />
      {/* rim light bord droit */}
      <path d="M 73.4 57.5 C 73 62.5 71.6 67 69.2 70.2" fill="none" stroke="#E5CC8F" strokeWidth="1" strokeLinecap="round" opacity="0.55" />
    </>
  ),
}
